import { CSSProperties } from "react";
import { Modal } from "react-bootstrap";
import { getPortraitStageContainer } from "../PortraitStage";

export type WishSubmitPhase = "closed" | "ready" | "submitting" | "submitted";

type WishSubmitModalProps = {
  phase: WishSubmitPhase;
  wish: string;
  onSubmit: () => void;
  onKeepEditing: () => void;
};

const Spinner = () => (
  <div className="wishSubmit__spinner" aria-hidden="true">
    {Array.from({ length: 12 }, (_, i) => (
      <span key={i} style={{ "--i": i } as CSSProperties} />
    ))}
  </div>
);

const CheckMark = () => (
  <svg className="wishSubmit__check" viewBox="0 0 120 120" aria-hidden="true">
    <circle cx="60" cy="60" r="60" fill="currentColor" />
    <path
      d="M34 63L51 80L88 40"
      fill="none"
      stroke="#fff"
      strokeWidth="12"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Release-my-wish flow: confirm (ready) → submitting → submitted.
 * Same structure as social-collage's ready / SubmitStatusModal.
 */
const WishSubmitModal = ({ phase, wish, onSubmit, onKeepEditing }: WishSubmitModalProps) => {
  const isReady = phase === "ready";

  return (
    <Modal
      container={getPortraitStageContainer}
      show={phase !== "closed"}
      // Only the confirm step can be dismissed; status steps are locked.
      onHide={isReady ? onKeepEditing : () => {}}
      backdrop={isReady ? true : "static"}
      keyboard={isReady}
      centered
      className="wishSubmitModal"
      aria-labelledby="wish-submit-title"
    >
      <Modal.Body className={`wishSubmit wishSubmit--${phase}`}>
        {isReady ? (
          <>
            <p className="wishSubmit__label">Your wish:</p>
            <blockquote className="wishSubmit__quote">“{wish}”</blockquote>
            <h2 id="wish-submit-title" className="wishSubmit__title">
              READY TO SUBMIT YOUR WISH?
            </h2>
            <div className="wishSubmit__actions">
              <button
                type="button"
                className="wishSubmit__button wishSubmit__button--submit"
                onClick={onSubmit}
              >
                YES, SUBMIT
              </button>
              <button
                type="button"
                className="wishSubmit__button wishSubmit__button--edit"
                onClick={onKeepEditing}
              >
                KEEP EDITING
              </button>
            </div>
          </>
        ) : phase === "submitting" ? (
          <>
            <Spinner />
            <h2 id="wish-submit-title" className="wishSubmit__title" role="status">
              SUBMITTING YOUR WISH
            </h2>
          </>
        ) : (
          <>
            <CheckMark />
            <h2 id="wish-submit-title" className="wishSubmit__title" role="status">
              YOUR WISH
              <br />
              HAS BEEN SUBMITTED SUCCESSFULLY!
            </h2>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default WishSubmitModal;
